// https://docs.sphere-engine.com/compilers/widget/integration
import _ from 'lodash';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { makeStyles, CircularProgress, Button, Typography, Tooltip } from 'design-system';
import {
  CodeAnnotation,
  CodeField,
  TestCase,
  TextCollectionAttachmentType,
} from '@scale/shared/types/params/textcollection';
import { CodeFieldAnnotation } from '@scale/shared/types/response/textcollection';
import TestCaseComponent from './TestCase';
import apiFetch from 'react-scale/lib/api-fetch';
import {
  CodeExecutionResult,
  CodeExecutionStatus,
  isStatusCompilingOrExecuting,
} from '@scale/shared/textcollection/defaults';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { MultipartFileUpload } from 'react-scale/components/scalers/tasks/GenericDataCollection/Uploader';
import {
  DataInfo,
  IOnUploadComplete,
} from 'react-scale/components/scalers/tasks/GenericDataCollection/types';
/* eslint-disable restrict-imports/restrict-imports */
import { FormHelperText } from '@material-ui/core';
import { getHelperText } from '../helperText';

interface CodeFieldProps {
  field: CodeField;
  isCustomer?: boolean; // to control hidden test cases
  annotation: CodeFieldAnnotation;
  setAnnotation: (annotation: CodeFieldAnnotation) => void;
  setSubmitError?: (field_id: string, error: string) => void;
  disabled?: boolean;
  projectId?: string;
  assignmentId?: string;
  userId?: string;
}

function CodeFieldSection({
  field,
  isCustomer,
  annotation,
  setAnnotation,
  setSubmitError: _setSubmitError,
  disabled,
  projectId,
  assignmentId,
  userId,
}: CodeFieldProps) {
  const classes = useStyles();
  const [widgetId, setWidgetId] = useState(Math.round(Math.random() * Number.MAX_SAFE_INTEGER));
  // only applies to visible test cases, hidden test cases are not checked here
  const [testResults, setTestResults] = useState<(CodeExecutionResult | null)[]>([]);
  const [codeRunsSuccessfully, setCodeRunsSuccessfully] = useState(false);
  const [passesTests, setPassesTests] = useState(false);
  const ignoreFailedTests = useUnleashFeatureFlag('ignore-failed-code-tests', { projectId }, false);
  const ignoreAllCodeValidation = useUnleashFeatureFlag(
    'ignore-all-code-validation',
    { projectId },
    false,
  );
  const [widgetInitialized, setWidgetInitialized] = useState(false);
  const [codeDirty, setCodeDirty] = useState(false);
  const [testsDirty, setTestsDirty] = useState(false);
  const [viewingFile, setViewingFile] = useState(false);
  const setSubmitError = useCallback(
    (error: string) => {
      _setSubmitError && _setSubmitError(field.field_id, error);
    },
    [_setSubmitError, field.field_id],
  );
  const currentResponse: CodeAnnotation = useMemo(
    () =>
      annotation.response[0] ?? {
        sourceCode: '',
        language: field.languages[0],
        testCases: _.cloneDeep(field.test_cases),
      },
    [annotation.response, field],
  );
  useEffect(() => {
    if (!setSubmitError) {
      return;
    }
    if (ignoreAllCodeValidation) {
      setSubmitError('');
      return;
    }
    if (!widgetInitialized) {
      setSubmitError(`Please wait for ${field.title} to load`);
    } else if (field.can_add_test_cases && !currentResponse.testCases?.length) {
      setSubmitError(`Please add at least one test case to ${field.title}`);
    } else if (codeDirty || !codeRunsSuccessfully) {
      setSubmitError(`Please ensure the code in ${field.title} compiles and executes successfully`);
    } else if (!ignoreFailedTests && !passesTests && !!currentResponse.testCases?.length) {
      setSubmitError(`Please ensure the code in ${field.title} passes all tests`);
    } else if (field.should_upload_media && !currentResponse.files?.length) {
      setSubmitError(`Please upload a file to ${field.title}`);
    } else {
      setSubmitError('');
    }
  }, [
    currentResponse,
    field,
    codeDirty,
    codeRunsSuccessfully,
    ignoreAllCodeValidation,
    ignoreFailedTests,
    passesTests,
    setSubmitError,
  ]);
  const runTests = useCallback(async () => {
    setTestResults([]);
    const results = await apiFetch<(CodeExecutionResult | null)[]>(
      null,
      `internal/textcollection/run_code_test_suite`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentResponse),
      },
    );
    const newPassesTests = results.every(
      (result, ind) =>
        !!result &&
        result.status === CodeExecutionStatus.Success &&
        result.output?.trim() === currentResponse.testCases?.[ind].output.trim(),
    );
    setPassesTests(newPassesTests);
    setTestResults(results);
    return newPassesTests;
  }, [currentResponse, setTestResults, setPassesTests]);
  const updateAnnotation = useCallback(
    (data: any) => {
      setCodeDirty(true);
      setTestResults([]);
      setPassesTests(false);
      setAnnotation({
        ...annotation,
        response: [
          {
            ...currentResponse,
            sourceCode: data.source,
          },
        ],
      });
      return true;
    },
    [annotation, currentResponse, setAnnotation],
  );
  const updatePreviousResponses = useCallback(
    async (data: any) => {
      if (codeDirty || testsDirty) {
        const newPassesTests = await runTests();
        const previousSubmissions = currentResponse.previousSubmissions ?? [];
        previousSubmissions.push({
          sourceCode: data.submissionSource,
          language: data.submissionLanguage,
          accepted: newPassesTests,
        });
        setAnnotation({
          ...annotation,
          response: [
            {
              ...currentResponse,
              sourceCode: data.submissionSource,
              language: data.submissionLanguage,
              previousSubmissions,
            },
          ],
        });
      }
      setCodeDirty(false);
      setTestsDirty(false);
      return true;
    },
    [
      annotation,
      currentResponse,
      setAnnotation,
      codeDirty,
      setCodeDirty,
      testsDirty,
      setTestsDirty,
      runTests,
    ],
  );
  const updateCanSubmit = useCallback(
    (data: any) => {
      if (isStatusCompilingOrExecuting(data.statusNumber)) {
        return;
      }

      if (data.statusNumber !== CodeExecutionStatus.Success) {
        setCodeRunsSuccessfully(false);
      } else {
        setCodeRunsSuccessfully(true);
      }
    },
    [setCodeRunsSuccessfully],
  );
  const addTestCase = () => {
    const newTestCase: TestCase = {
      input: '',
      output: '',
    };
    const testCases = currentResponse.testCases ?? [];
    testCases.push(newTestCase);
    const newTestResults = [...testResults, null];
    setTestResults(newTestResults);
    setTestsDirty(true);
    setAnnotation({
      ...annotation,
      response: [
        {
          ...currentResponse,
          testCases,
        },
      ],
    });
  };
  const removeTestCase = (idx: number, isHidden: boolean) => {
    if (isHidden) {
      const hiddenTestCases = currentResponse.hiddenTestCases ?? [];
      hiddenTestCases.splice(idx, 1);
      setAnnotation({
        ...annotation,
        response: [
          {
            ...currentResponse,
            hiddenTestCases,
          },
        ],
      });
    } else {
      const testCases = currentResponse.testCases ?? [];
      testCases.splice(idx, 1);
      const newTestResults = [...testResults];
      newTestResults.splice(idx, 1);
      setTestResults(newTestResults);
      setAnnotation({
        ...annotation,
        response: [
          {
            ...currentResponse,
            testCases,
          },
        ],
      });
    }
    setTestsDirty(true);
  };
  const updateTestCase = (idx: number, testCase: TestCase, isHidden: boolean) => {
    if (isHidden) {
      const hiddenTestCases = currentResponse.hiddenTestCases ?? [];
      hiddenTestCases[idx] = testCase;
      setAnnotation({
        ...annotation,
        response: [
          {
            ...currentResponse,
            hiddenTestCases,
          },
        ],
      });
    } else {
      const testCases = currentResponse.testCases ?? [];
      testCases[idx] = testCase;
      const newTestResults = [...testResults];
      newTestResults[idx] = null;
      setTestResults(newTestResults);
      setAnnotation({
        ...annotation,
        response: [
          {
            ...currentResponse,
            testCases,
          },
        ],
      });
    }
    setTestsDirty(true);
  };
  const toggleTestCaseVisibility = (idx: number, isHidden: boolean) => {
    if (isHidden) {
      const hiddenTestCases = currentResponse.hiddenTestCases ?? [];
      const testCases = currentResponse.testCases ?? [];
      const newTestCase = hiddenTestCases.splice(idx, 1)[0];
      testCases.push(newTestCase);
      const newTestResults = [...testResults, null];
      setTestResults(newTestResults);
      setAnnotation({
        ...annotation,
        response: [
          {
            ...currentResponse,
            testCases,
            hiddenTestCases,
          },
        ],
      });
    } else {
      const hiddenTestCases = currentResponse.hiddenTestCases ?? [];
      const testCases = currentResponse.testCases ?? [];
      const newTestCase = testCases.splice(idx, 1)[0];
      hiddenTestCases.push(newTestCase);
      setAnnotation({
        ...annotation,
        response: [
          {
            ...currentResponse,
            testCases,
            hiddenTestCases,
          },
        ],
      });
    }
  };

  // *** START widget initialization code ***
  useEffect(() => {
    // https://docs.sphere-engine.com/compilers/widget/integration
    const SCRIPT_ID = 'sphere-engine-compilers-initialization-script';
    if (document.body.contains(document.getElementById(SCRIPT_ID))) {
      // should only ever initialize once
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.innerHTML = `SEC_HTTPS = true;
    SEC_BASE = "compilers.widgets.sphere-engine.com"; 
    (function(d, s, id){ SEC = window.SEC || (window.SEC = []);
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return; js = d.createElement(s); js.id = id; 
      js.src = (SEC_HTTPS ? "https" : "http") + "://" + SEC_BASE + "/static/sdk/sdk.js";
      fjs.parentNode.insertBefore(js, fjs);   
    }(document, "script", "sphere-engine-compilers-jssdk"));
    SEC.ready=function(e){"loading"!=document.readyState&&"interactive"!=document.readyState?e():window.addEventListener("load",e)};`;
    document.body.appendChild(script);
  }, []);
  // sometimes the widget fails to load
  const widgetBroken = !!document.getElementById(`unloaded-widget-${field.field_id}-${widgetId}`);
  const initializeWidget = useCallback(() => {
    try {
      // @ts-ignore
      const widget = window.SEC?.widget?.(`widget-${field.field_id}-${widgetId}`);
      if (!widget) {
        return false;
      }
      widget.config({
        code: {
          compiler: currentResponse.language,
          source: currentResponse.sourceCode,
          input: field.default_input ?? currentResponse.testCases?.[0]?.input,
        },
        compilers: {
          list: field.languages,
        },
      });
      setWidgetInitialized(true);
      return true;
    } catch (_e) {
      // suppress console errors
      return false;
    }
  }, [currentResponse, field, widgetId, setWidgetInitialized]);
  const listenersAdded = useCallback(() => {
    try {
      // @ts-ignore
      const widget = window.SEC?.widget?.(`widget-${field.field_id}-${widgetId}`);
      return !!widget?.events.functions?.length;
    } catch (_e) {
      // suppress console errors
      return false;
    }
  }, [field.field_id, widgetId]);
  const addListeners = useCallback(() => {
    try {
      // @ts-ignore
      const widget = window.SEC?.widget?.(`widget-${field.field_id}-${widgetId}`);
      if (!widget) {
        return false;
      }
      if (listenersAdded()) {
        widget.events.functions = [];
      }
      if (!disabled) {
        // https://docs.sphere-engine.com/compilers/widget/integration#javascript-sdk-events
        // for some reason this does not work when you make it an enum
        widget.events.subscribe('sourceChanged', updateAnnotation);
        widget.events.subscribe('beforeSendSubmission', updatePreviousResponses);
        widget.events.subscribe('checkStatus', updateCanSubmit);
      }
      return true;
    } catch (_e) {
      // suppress console errors
      return false;
    }
  }, [
    updateAnnotation,
    updatePreviousResponses,
    updateCanSubmit,
    disabled,
    field.field_id,
    widgetId,
    listenersAdded,
  ]);
  useEffect(() => {
    let checker: NodeJS.Timeout | undefined;
    // this setTimeout is relevant to main widget initialization logic, which may take a few seconds to initialize
    setTimeout(() => {
      if (widgetBroken) {
        setWidgetId(widgetId + 1);
      } else {
        checker = setInterval(() => {
          if (!widgetInitialized && !initializeWidget()) {
            setWidgetId(widgetId + 1);
          }
          if (addListeners()) {
            checker && clearInterval(checker);
            checker = undefined;
          }
        }, 1000);
      }
    }, 3000);
    // we also want to update listeners whenever possible if the widget is already set up
    if (widgetInitialized) {
      addListeners();
    }
    return () => {
      checker && clearInterval(checker);
    };
  }, [widgetId, initializeWidget, addListeners, listenersAdded, setWidgetInitialized]);
  // *** END widget initialization code ***

  const [fileInfo, setFileInfo] = useState<DataInfo>({
    unsignedS3DataUrl: '',
    unsignedDataUrl: '',
    fileId: '',
    contentType: '',
    dataChunkInfo: [undefined],
  });
  const setSignedDataUrl = (signedDataUrl: string) =>
    setFileInfo(fileInfo => ({ ...fileInfo, signedDataUrl }));
  const onUploadComplete = (payload: IOnUploadComplete) => {
    setFileInfo(_.omit(payload, ['dataIndex', 'dataChunkIndex']));
  };
  const [progress, setUploadProgress] = useState(0);
  useEffect(() => {
    const fileUrl = fileInfo.unsignedDataUrl || fileInfo.unsignedS3DataUrl || '';
    setAnnotation({
      ...annotation,
      response: [
        {
          ...currentResponse,
          files: fileUrl ? [fileUrl] : [],
        },
      ],
    });
  }, [fileInfo, setAnnotation]);
  return (
    <div className={classes.container}>
      <div className={classes.title}>{field.title}</div>
      <FormHelperText classes={{ root: classes.helperText }}>
        {getHelperText({ description: field.description })}
      </FormHelperText>
      <div className={classes.widget}>
        <div // this div is replaced by the actual widget by above script
          id={`unloaded-widget-${field.field_id}-${widgetId}`}
          className="sec-widget"
          data-id={`widget-${field.field_id}-${widgetId}`}
          data-widget="b052cff4e6afa0ac34cda19aa0f34ffd"
        />
        {widgetBroken && (
          <Tooltip title="click to refresh">
            <div className={classes.button} onClick={() => setWidgetId(widgetId + 1)}>
              <CircularProgress size={25} />
            </div>
          </Tooltip>
        )}
      </div>
      {!!field.should_upload_media && (
        <div className={classes.fileUpload}>
          <Typography className={classes.textHeader}>File Upload</Typography>
          <MultipartFileUpload
            projectId={projectId}
            dataToCollect={{ name: field.field_id, type: TextCollectionAttachmentType.Image }}
            userId={userId}
            assignmentId={assignmentId}
            setSignedDataUrl={setSignedDataUrl}
            setFileInfo={setFileInfo}
            fileInfo={fileInfo}
            uploadProgress={progress}
            setUploadProgress={setUploadProgress}
            onUploadComplete={onUploadComplete}
            fileIndex={0}
          />
          {!!currentResponse.files?.length && (
            <Button className={classes.fileViewButton} onClick={() => setViewingFile(!viewingFile)}>
              {viewingFile ? 'Hide' : 'View'} File
            </Button>
          )}
          {!!currentResponse.files?.length && viewingFile && <img src={currentResponse.files[0]} />}
        </div>
      )}
      {(!!currentResponse.testCases?.length ||
        !!currentResponse.hiddenTestCases?.length ||
        !!field.can_add_test_cases) && (
        <div className={classes.testCases}>
          <Typography className={classes.textHeader}>Test Cases</Typography>
          {!!currentResponse.testCases?.length &&
            currentResponse.testCases.map((testCase, idx) => (
              <TestCaseComponent
                name={`Test Case ${idx + 1}`}
                key={idx}
                testCase={testCase}
                editable={idx >= field.test_cases!.length}
                onDelete={() => removeTestCase(idx, false)}
                setInput={input => updateTestCase(idx, { ...testCase, input }, false)}
                setOutput={output => updateTestCase(idx, { ...testCase, output }, false)}
                toggleVisibility={
                  isCustomer ? () => toggleTestCaseVisibility(idx, false) : undefined
                }
                result={testResults[idx]}
              />
            ))}
          {isCustomer &&
            !!currentResponse.hiddenTestCases?.length &&
            currentResponse.hiddenTestCases.map((testCase, idx) => (
              <TestCaseComponent
                name={`Hidden Test Case ${idx + 1}`}
                key={idx}
                testCase={testCase}
                editable={true}
                isHidden={true}
                onDelete={() => removeTestCase(idx, true)}
                setInput={input => updateTestCase(idx, { ...testCase, input }, true)}
                setOutput={output => updateTestCase(idx, { ...testCase, output }, true)}
                toggleVisibility={() => toggleTestCaseVisibility(idx, true)}
              />
            ))}
          {!!field.can_add_test_cases && <Button onClick={addTestCase}>+ Add test case</Button>}
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.colors.gray10}`,
    borderRadius: 4,
    boxShadow: '0px 0px 4px rgba(17, 24, 39, 0.1)',
    background: theme.colors.white,
  },
  title: {
    color: '#000000',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '8px',
  },
  helperText: {
    marginBottom: 8,
  },
  widget: {
    width: '100%',
    overflow: 'scroll',
  },
  textHeader: {
    fontWeight: 'bold',
  },
  fileViewButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: 'fit-content',
  },
  fileUpload: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    width: '100%',
  },
  testCases: {
    marginTop: theme.spacing(2),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  button: {
    cursor: 'pointer',
    width: 'fit-content',
  },
}));

export default CodeFieldSection;
